import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, User, Calendar, Tag, Clock, Shield } from 'lucide-react';
import { auditApi, usersApi } from '../services/api';
import './AuditTrail.css';

// Action categories for filtering
const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: 'deal_created', label: 'Deal Created' },
  { value: 'deal_deleted', label: 'Deal Deleted' },
  { value: 'deal_status_changed', label: 'Deal Status Changed' },
  { value: 'document_uploaded', label: 'Document Uploaded' },
  { value: 'member_added', label: 'Member Added' },
  { value: 'member_removed', label: 'Member Removed' },
  { value: 'member_role_changed', label: 'Role Changed' },
  { value: 'permissions_updated', label: 'Permissions Updated' },
  { value: 'contact_created', label: 'Contact Created' },
  { value: 'login', label: 'Login' },
];

const ACTION_COLORS = {
  deal_created: '#16a34a',
  deal_deleted: '#dc2626',
  deal_status_changed: '#d97706',
  document_uploaded: '#2563eb',
  member_added: '#7c3aed',
  member_removed: '#dc2626',
  member_role_changed: '#d97706',
  permissions_updated: '#0891b2',
  contact_created: '#16a34a',
  login: '#64748b',
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function formatAction(action) {
  return (action || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const AuditTrail = ({ currentUser }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [members, setMembers] = useState([]);

  // Filters
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterUser) params.userId = filterUser;
      if (filterAction) params.action = filterAction;
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate) params.endDate = filterEndDate;

      const res = await auditApi.list(params);
      setLogs(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load audit logs.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterAction, filterStartDate, filterEndDate]);

  // Fetch members for filter dropdown
  useEffect(() => {
    usersApi.list().then(res => setMembers(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleClearFilters = () => {
    setFilterUser('');
    setFilterAction('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const hasFilters = filterUser || filterAction || filterStartDate || filterEndDate;

  if (currentUser?.role !== 'admin') {
    return (
      <div className="audit-access-denied">
        <Shield size={32} />
        <p>Audit Trail is only accessible to administrators.</p>
      </div>
    );
  }

  return (
    <div className="audit-trail">
      {/* Filters */}
      <div className="audit-filters">
        <div className="audit-filter-group">
          <label><User size={13} /> User</label>
          <select value={filterUser} onChange={e => setFilterUser(e.target.value)} className="audit-select">
            <option value="">All Users</option>
            {members.map(m => (
              <option key={m.id} value={m.id}>{m.name || m.email}</option>
            ))}
          </select>
        </div>

        <div className="audit-filter-group">
          <label><Tag size={13} /> Action Type</label>
          <select value={filterAction} onChange={e => setFilterAction(e.target.value)} className="audit-select">
            {ACTION_TYPES.map(a => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
        </div>

        <div className="audit-filter-group">
          <label><Calendar size={13} /> From</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={e => setFilterStartDate(e.target.value)}
            className="audit-date-input"
          />
        </div>

        <div className="audit-filter-group">
          <label><Calendar size={13} /> To</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={e => setFilterEndDate(e.target.value)}
            className="audit-date-input"
          />
        </div>

        <div className="audit-filter-actions">
          {hasFilters && (
            <button className="audit-clear-btn" onClick={handleClearFilters}>
              Clear Filters
            </button>
          )}
          <button className="audit-refresh-btn" onClick={fetchLogs} title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Results count */}
      <div className="audit-results-bar">
        <span className="audit-count">
          {!loading && `${logs.length} event${logs.length !== 1 ? 's' : ''}`}
          {!loading && hasFilters && ' (filtered)'}
        </span>
      </div>

      {/* Error */}
      {error && <div className="audit-error">{error}</div>}

      {/* Table */}
      {!loading && logs.length === 0 && !error && (
        <div className="audit-empty">
          <Clock size={32} />
          <p>No audit events found{hasFilters ? ' for the selected filters' : ''}.</p>
          {!hasFilters && <p className="audit-empty-hint">Events will appear here as users take actions on the platform.</p>}
        </div>
      )}

      {!loading && logs.length > 0 && (
        <div className="audit-table-wrapper">
          <table className="audit-table">
            <thead>
              <tr>
                <th>Date &amp; Time</th>
                <th>User</th>
                <th>Action</th>
                <th>Resource</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id}>
                  <td className="audit-date">{formatDate(log.created_at)}</td>
                  <td className="audit-user">{log.user_name || log.user_id?.slice(0, 8) || '—'}</td>
                  <td>
                    <span
                      className="audit-action-badge"
                      style={{ background: (ACTION_COLORS[log.action] || '#64748b') + '18', color: ACTION_COLORS[log.action] || '#64748b' }}
                    >
                      {formatAction(log.action)}
                    </span>
                  </td>
                  <td className="audit-resource">{log.resource || '—'}</td>
                  <td className="audit-details">{log.details || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditTrail;
